import React, { useState, useEffect } from 'react';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch, getMarketingSiteApiUrl } from '../utils/api';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Resolve image URLs - convert production domain to relative path on non-production environments
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (typeof window !== 'undefined' && !window.location.hostname.includes('popcrownofficial.com')) {
      return url.replace('https://popcrownofficial.com', '');
    }
    return url;
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const baseUrl = getMarketingSiteApiUrl();
        const response = await fetch(`${baseUrl}/api/blog/posts?format=html`);
        const data = await response.json();
        const posts = data.posts || [];
        // Format date for display
        const formattedPosts = posts.map(post => ({
          ...post,
          displayDate: new Date(post.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }));
        setBlogPosts(formattedPosts);
      } catch (error) {
        setFetchError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Generate schema dynamically from posts
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "PopCrown Blog - Michael Jackson Fan Stories & Pop Culture",
    "description": "Unofficial fan stories, articles and pop culture celebrations about Michael Jackson, the King of Pop. Read personal stories from MJ fans worldwide at PopCrown.",
    "url": "https://popcrownofficial.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "PopCrown Official",
      "url": "https://popcrownofficial.com"
    },
    "blogPost": blogPosts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://popcrownofficial.com/blog/${post.slug}`,
      "description": post.excerpt,
      "author": {"@type": "Person", "name": post.author, "url": "https://popcrownofficial.com/about"},
      "datePublished": post.date,
      "image": post.image,
      "keywords": post.keywords || `Michael Jackson, King of Pop, ${post.category}`
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com"},
      {"@type": "ListItem", "position": 2, "name": "Blog", "item": "https://popcrownofficial.com/blog"}
    ]
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="PopCrown Blog — Michael Jackson Fan Stories, News & Opinion"
        description="Read honest, unfiltered blogs from PopCrown's co-founders. Michael Jackson fan stories, biopic news, community updates, and the truth about MJ's legacy."
        keywords="PopCrown blog, Michael Jackson blog, MJ fan stories, Michael Jackson news, King of Pop blog, Michael Jackson biopic news, MJ community, Tasha PopCrown, Michael Jackson opinion, fan perspective"
        canonical="/blog"
        image="https://popcrownofficial.com/hero_this_is_it.jpg"
      />
      {/* Schema.org JSON-LD for Blog Page */}
      <JsonLd data={blogSchema} />
      <JsonLd data={breadcrumbSchema} />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image - Record Store with "This Is It" - SEO optimized */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/hero_this_is_it.jpg)',
          }}
          role="img"
          aria-label="Vintage record store featuring Michael Jackson This Is It album poster alongside classic vinyl records including Pink Floyd, The Beatles Abbey Road, Carlos Santana, and Bob Dylan - Photo by Esra Nur Kalay"
        />
        {/* Hidden img tag for SEO crawlers */}
        <img 
          src="/hero_this_is_it.jpg" 
          alt="Vintage record store vinyl collection featuring Michael Jackson This Is It album poster - iconic music memorabilia display with classic albums including Pink Floyd Dark Side of the Moon, The Beatles Abbey Road, Carlos Santana, Bob Dylan concert posters - perfect imagery for MJ fans and music collectors celebrating King of Pop legacy"
          className="sr-only"
          width="1920"
          height="1407"
          loading="eager"
        />
        {/* Overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(225, 6, 0, 0.3) 100%)'
        }} />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 text-[#E10600] mx-auto mb-6 crown-pulse object-contain" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="thriller-text">Michael Jackson Fan Blog</span>
          </h1>
          <p className="text-xl text-white mb-4 max-w-2xl mx-auto">
            Stories, opinions and culture from the Michael Jackson fan community
          </p>
          <p className="text-sm text-gray-300 mb-8">
            (Not affiliated with any estates or rights holders)
          </p>
          <div className="mx-auto h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
        </div>
      </section>

      {/* Photo Credit */}
      <div className="bg-black py-2">
        <p className="text-xs text-gray-400 text-center">
          Hero image: "CDs and Records in a Record Store" by{' '}
          <a 
            href="https://www.pexels.com/@esranurkalay/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-[#E10600] transition-colors underline"
          >
            Esra Nur Kalay
          </a>
          {' '}via{' '}
          <a 
            href="https://www.pexels.com/photo/cds-and-records-in-a-record-store-12342980/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-[#E10600] transition-colors underline"
          >
            Pexels
          </a>
          {' '}(Free to use)
        </p>
      </div>

      {/* Blog Posts Grid */}
      <section className="py-20" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#DC143C]"></div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article key={post.id} className="overflow-hidden rounded-xl transition-all hover:transform hover:scale-105" style={{
                background: '#FFFFFF',
                border: '2px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}>
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={resolveImageUrl(post.image)} 
                    alt={`${post.title} - Michael Jackson fan blog article - PopCrown MJ community King of Pop ${post.category} content`}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#E10600] text-black px-3 py-1 rounded-full text-xs font-bold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.displayDate || new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-black mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="text-[#DC143C] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20" style={{
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-black mb-4">
              Never Miss a <span className="thriller-text">Story</span>
            </h2>
            <p className="text-gray-600 mb-6">
              Subscribe to our newsletter for weekly fan stories, news, and exclusive content
            </p>
            <Link to="/" className="btn-gold inline-block">
              Subscribe to Newsletter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;